import React, { useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import {
  AppBar,
  Avatar,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  AccountCircle,
  DarkMode,
  LightMode,
  Logout,
  Menu as MenuIcon,
  Search as SearchIcon,
  Settings,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useThemeMode } from "../../ThemeProviderContext";
import { useCommandPalette } from "../../hooks/useCommandPalette";
import ev91Logo from "../../assets/vehicle-icons/voice-logo-91-removebg-preview.png";
import SparePartsNotificationBell from "../../modules/spare-parts/notifications/SparePartsNotificationBell";

interface TopbarProps {
  onMenuClick: () => void;
  onSidebarToggle: () => void;
}

const headerIconSx = (theme: any) => ({
  width: 30,
  height: 30,
  color: theme.palette.mode === "dark" ? "#EAF6FF" : "#0F172A",
  border: "1px solid transparent",
  backgroundColor: "transparent",
  transition: "all .22s ease",
  "& .MuiSvgIcon-root": {
    fontSize: 18,
  },
  "&:hover": {
  transform: "none",
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(255,255,255,0.06)"
        : "rgba(255,255,255,0.32)",
  },
});

export default function Topbar({
  onMenuClick,
  onSidebarToggle,
}: TopbarProps) {
  const { user, logout } = useAuth();
  const theme = useTheme();
  const { mode, setMode } = useThemeMode();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const navigate = useNavigate();
  const { open } = useCommandPalette();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) =>
    setAnchorEl(event.currentTarget);
  const handleProfileMenuClose = () => setAnchorEl(null);

  const handleSettings = () => {
    setAnchorEl(null);
    navigate("/settings");
  };

  const handleProfile = () => {
    setAnchorEl(null);
    navigate("/profile");
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (err) {
      console.error("Logout Failed", err);
    }
    handleProfileMenuClose();
  };

  const renderMenu = (
    <Menu
      anchorEl={anchorEl}
      id="primary-search-account-menu"
      open={Boolean(anchorEl)}
      onClose={handleProfileMenuClose}
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
    >
      <MenuItem onClick={handleProfile}>
        <AccountCircle sx={{ mr: 1 }} /> Profile
      </MenuItem>
      <MenuItem onClick={handleSettings}>
        <Settings sx={{ mr: 1 }} /> Settings
      </MenuItem>
      <MenuItem onClick={handleLogout}>
        <Logout sx={{ mr: 1 }} /> Logout
      </MenuItem>
    </Menu>
  );

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
  zIndex: theme.zIndex.drawer + 1,
  top: 0,
  left: 0,
  right: 0,
  width: "100%",
  px: 0,
  py: 0,

  color: theme.palette.mode === "dark"
    ? "#E5EDF8"
    : "#0F172A",

background: "transparent",
backgroundColor: "transparent",
backgroundImage: "none",

borderBottom: "none",

boxShadow: "none",

  pointerEvents: "none",
}}
      >
        <Toolbar
          sx={{
            minHeight: { xs: 56, md: 64 },
            gap: { xs: 0.75, md: 1 },
            px: 0,
            pointerEvents: "auto",
          }}
        >
          <IconButton
            color="inherit"
            edge="start"
            onClick={isMobile ? onMenuClick : onSidebarToggle}
            sx={headerIconSx(theme)}
          >
            <MenuIcon />
          </IconButton>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              minWidth: { xs: 110, md: 240 },
              px: { xs: 0.25, sm: 0.75 },
            }}
          >
            <Box
              component="img"
              src={ev91Logo}
              alt="EV91"
              sx={{
                width: { xs: 50, md: 60 },
                height: { xs: 50, md: 60 },
                objectFit: "contain",
                filter:
                  theme.palette.mode === "dark"
                    ? "drop-shadow(0 8px 16px rgba(59,130,246,0.28))"
                    : "drop-shadow(0 8px 16px rgba(37,99,235,0.16))",
              }}
            />
            <Box sx={{ display: { xs: "none", sm: "block" }, minWidth: 0 }}>
              <Typography
                variant="h6"
                sx={{
                  color: "inherit",
                  fontFamily: "'Orbitron', sans-serif",
                  fontWeight: 800,
                  letterSpacing: "2px",
                  lineHeight: 0.95,
                  textTransform: "uppercase",
                  fontSize: { sm: 18, md: 22 },
                }}
              >
              EV91
            </Typography>
              <Typography
                variant="caption"
                sx={{
                  color:
                    theme.palette.mode === "dark"
                      ? "rgba(226,232,240,0.74)"
                      : "rgba(51,65,85,0.72)",
                  fontWeight: 800,
                  letterSpacing: 0,
                  textTransform: "uppercase",
                  fontSize: { sm: 10, md: 12 },
                }}
              >
                Technologies
              </Typography>
            </Box>
            <Typography
              variant="h6"
              sx={{
                display: { xs: "block", sm: "none" },
                color: "inherit",
                fontWeight: 900,
              }}
            >
              EV91
            </Typography>
          </Box>

          <Box
            sx={{
              flexGrow: 1,
              display: "flex",
              justifyContent: "flex-end",
              px: { xs: 0, md: 0 },
            }}
          >
            {!isMobile && (
              <IconButton
                color="inherit"
                onClick={open}
                sx={{
                  ...headerIconSx(theme),
                  mr: { md: 1.5 },
                }}
              >
                <SearchIcon />
              </IconButton>
            )}
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: 0.35, md: 0.85 },
            }}
          >
            {isMobile && (
              <IconButton color="inherit" onClick={open} sx={headerIconSx(theme)}>
                <SearchIcon />
              </IconButton>
            )}

            <IconButton
              color="inherit"
              onClick={() => setMode(mode === "light" ? "dark" : "light")}
              sx={headerIconSx(theme)}
            >
              {mode === "light" ? <DarkMode /> : <LightMode />}
            </IconButton>

            <Box sx={headerIconSx(theme)}>
              <SparePartsNotificationBell color="inherit" />
            </Box>

            <IconButton
              edge="end"
              color="inherit"
              onClick={handleProfileMenuOpen}
              sx={{ p: 0.35 }}
            >
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: theme.palette.mode === "dark" ? "#2563EB" : "#0F172A",
                  fontWeight: 900,
                  fontSize: "0.78rem",
                  border:
                    theme.palette.mode === "dark"
                      ? "2px solid rgba(226,232,240,0.72)"
                      : "2px solid rgba(255,255,255,0.94)",
                  boxShadow:
                    theme.palette.mode === "dark"
                      ? "0 10px 24px rgba(0,0,0,0.28)"
                      : "0 10px 24px rgba(15,23,42,0.14)",
                }}
              >
                {user?.firstName?.[0]}
                {user?.lastName?.[0]}
              </Avatar>
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {renderMenu}
    </Box>
  );
}
